import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Fade,
  Zoom,
  CircularProgress,
  Alert,
  Avatar,
  Tabs,
  Tab
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PeopleIcon from '@mui/icons-material/People';

import SimilarPlayersTab from './SimilarPlayersTab';

import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip
} from 'recharts';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';

// Fonctions de conversion
function feetInchesToCm(feet, inches) {
  const f = parseFloat(feet) || 0;
  const i = parseFloat(inches) || 0;
  const totalInches = f * 12 + i;
  return totalInches * 2.54;
}

function inchesToCm(inches) {
  const i = parseFloat(inches) || 0;
  return i * 2.54;
}

function lbsToKg(lbs) {
  const l = parseFloat(lbs) || 0;
  return l * 0.45359237;
}

function cmToFeetInches(cm) {
  const c = parseFloat(cm) || 0;
  const totalInches = c / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return { feet, inches: inches.toFixed(2) };
}

function cmToInches(cm) {
  const c = parseFloat(cm) || 0;
  return (c / 2.54).toFixed(2);
}

function kgToLbs(kg) {
  const k = parseFloat(kg) || 0;
  return (k / 0.45359237).toFixed(2);
}

/**
 * Composant d'onglet pour gérer les mesures (medidas) d'un joueur, 
 * affichées sous forme de tableau comparatif avec indicateurs d'évolution.
 */
const calculateDecimalAge = (dateNaissance, dateRef) => {
  if (!dateNaissance || !dateRef) return '';
  const birth = new Date(dateNaissance);
  const ref = new Date(dateRef);
  let years = ref.getFullYear() - birth.getFullYear();
  const m = ref.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < birth.getDate())) {
    years--;
  }
  const diffTime = Math.abs(ref - birth);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const decimal = (diffDays % 365.25) / 365.25;
  return (years + decimal).toFixed(1);
};

const MedidasTab = ({ playerId, dateNaissance, onDataLoaded }) => {
  const { t } = useTranslation();
  const [medidasData, setMedidasData] = useState([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentMedida, setCurrentMedida] = useState(null);
  const [editedMedida, setEditedMedida] = useState({});
  // États pour les valeurs US
  const [usValues, setUsValues] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [medidaToDelete, setMedidaToDelete] = useState(null);
  const [hoveredMetric, setHoveredMetric] = useState(null);
  const [comparativeData, setComparativeData] = useState(null);
  const [loadingComparative, setLoadingComparative] = useState(false);
  const [rankingDialogOpen, setRankingDialogOpen] = useState(false);
  const [rankingDialogData, setRankingDialogData] = useState(null);
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [rankingMetricLabel, setRankingMetricLabel] = useState('');
  const [rankingMetricUnit, setRankingMetricUnit] = useState('');
  const [rankingTypeFilter, setRankingTypeFilter] = useState('');
  const [rankingMetricKey, setRankingMetricKey] = useState('');
  const [comparativeTypeFilter, setComparativeTypeFilter] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;

  // Configuration des métriques avec unités, couleurs et descriptions (dynamique avec i18n)
  const metrics = React.useMemo(() => [
    // === ANTHROPOMETRÍA ===
    {
      key: 'altura',
      label: t('measurements.labels.altura'),
      unit: 'cm',
      color: '#2196F3',
      category: t('measurements.categories.anthropometry'),
      description: t('measurements.descriptions.altura'),
      example: t('measurements.examples.altura')
    },
    {
      key: 'envergadura',
      label: t('measurements.labels.envergadura'),
      unit: 'cm',
      color: '#00BCD4',
      category: t('measurements.categories.anthropometry'),
      description: t('measurements.descriptions.envergadura'),
      example: t('measurements.examples.envergadura')
    },
    {
      key: 'peso',
      label: t('measurements.labels.peso'),
      unit: 'kg',
      color: '#FF9800',
      category: t('measurements.categories.anthropometry'),
      description: t('measurements.descriptions.peso'),
      example: t('measurements.examples.peso')
    },
    {
      key: 'longitud_mano',
      label: t('measurements.labels.longitudMano'),
      unit: 'cm',
      color: '#795548',
      category: t('measurements.categories.anthropometry'),
      description: t('measurements.descriptions.longitudMano'),
      example: t('measurements.examples.longitudMano')
    },
    {
      key: 'ancho_mano',
      label: t('measurements.labels.anchoMano'),
      unit: 'cm',
      color: '#607D8B',
      category: t('measurements.categories.anthropometry'),
      description: t('measurements.descriptions.anchoMano'),
      example: t('measurements.examples.anchoMano')
    },
    {
      key: 'grasa_corporal',
      label: t('measurements.labels.grasaCorporal'),
      unit: '%',
      color: '#FF5722',
      inverse: true,
      category: t('measurements.categories.anthropometry'),
      description: t('measurements.descriptions.grasaCorporal'),
      example: t('measurements.examples.grasaCorporal')
    },
    // === VARIABLES DE RENDIMIENTO ===
    {
      key: 'alcance',
      label: t('measurements.labels.alcance'),
      unit: 'cm',
      color: '#009688',
      category: t('measurements.categories.performance'),
      description: t('measurements.descriptions.alcance'),
      example: t('measurements.examples.alcance')
    },
    {
      key: 'salto_vertical_maximo',
      label: t('measurements.labels.saltoVerticalMaximo'),
      unit: 'cm',
      color: '#4CAF50',
      category: t('measurements.categories.performance'),
      description: t('measurements.descriptions.saltoVerticalMaximo'),
      example: t('measurements.examples.saltoVerticalMaximo')
    },
    {
      key: 'salto_vertical_de_pie',
      label: t('measurements.labels.saltoVerticalDePie'),
      unit: 'cm',
      color: '#8BC34A',
      category: t('measurements.categories.performance'),
      description: t('measurements.descriptions.saltoVerticalDePie'),
      example: t('measurements.examples.saltoVerticalDePie')
    },
    {
      key: 'agilidad',
      label: t('measurements.labels.agilidad'),
      unit: 's',
      color: '#E91E63',
      inverse: true,
      category: t('measurements.categories.performance'),
      description: t('measurements.descriptions.agilidad'),
      example: t('measurements.examples.agilidad')
    },
    {
      key: 'sprint_cuarto',
      label: t('measurements.labels.sprintCuarto'),
      unit: 's',
      color: '#F44336',
      inverse: true,
      category: t('measurements.categories.performance'),
      description: t('measurements.descriptions.sprintCuarto'),
      example: t('measurements.examples.sprintCuarto')
    }
  ], [t]);

  // Charger les données
  useEffect(() => {
    const fetchMedidasData = async () => {
      if (!playerId) return;
      try {
        const response = await api.get('/api/medidas', {
          params: { playerId }
        });
        // Trier par date décroissante (plus récent en premier)
        const sortedData = response.data.sort((a, b) =>
          new Date(b.fecha) - new Date(a.fecha)
        );
        setMedidasData(sortedData);
        if (onDataLoaded) {
          onDataLoaded(sortedData.length > 0);
        }
        // Charger automatiquement les données comparatives si des mesures existent
        if (sortedData.length > 0) {
          fetchComparativeData();
        }
      } catch (error) {
        console.error(t('measurements.errors.fetchError'), error);
      }
    };
    fetchMedidasData();
  }, [playerId, backendUrl, section, onDataLoaded, t]);

  // Charger les données comparatives
  const fetchComparativeData = async (typeFilter) => {
    if (!playerId) return;

    setLoadingComparative(true);
    try {
      const params = { playerId };
      const filterType = typeFilter !== undefined ? typeFilter : comparativeTypeFilter;
      if (filterType) params.playerType = filterType;
      const response = await api.get('/api/medidas/comparative', { params });
      setComparativeData(response.data);
      // Initialiser le filtre au type du joueur si premier chargement
      if (comparativeTypeFilter === null && response.data?.playerInfo?.playerType) {
        setComparativeTypeFilter(response.data.playerInfo.playerType.toLowerCase());
      }
    } catch (error) {
      console.error(t('measurements.errors.fetchComparativeError'), error);
    } finally {
      setLoadingComparative(false);
    }
  };

  // Charger le classement détaillé pour une métrique
  const fetchRankingDetail = async (metricKey, metricLabel, typeFilter) => {
    const metricConfig = metrics.find(m => m.key === metricKey);
    setRankingMetricLabel(metricLabel);
    setRankingMetricKey(metricKey);
    setRankingMetricUnit(metricConfig?.unit || '');
    setRankingDialogOpen(true);
    setLoadingRanking(true);
    setRankingDialogData(null);
    const filterType = typeFilter !== undefined ? typeFilter : rankingTypeFilter;
    try {
      const params = { playerId, metric: metricKey };
      if (filterType) params.playerType = filterType;
      const response = await api.get('/api/medidas/ranking-detail', { params });
      setRankingDialogData(response.data);
    } catch (error) {
      console.error(t('measurements.errors.fetchRankingError'), error);
    } finally {
      setLoadingRanking(false);
    }
  };

  // Calculer l'évolution entre deux valeurs
  const calculateEvolution = (currentValue, previousValue, inverse = false) => {
    if (!currentValue || !previousValue) return null;
    const current = parseFloat(currentValue);
    const previous = parseFloat(previousValue);
    if (isNaN(current) || isNaN(previous)) return null;
    
    const diff = current - previous;
    const percentChange = ((diff / previous) * 100).toFixed(1);
    
    let trend = 'stable';
    if (Math.abs(diff) > 0.01) {
      trend = inverse ? (diff < 0 ? 'up' : 'down') : (diff > 0 ? 'up' : 'down');
    }
    
    return { diff, percentChange, trend };
  };

  // Obtenir l'icône et la couleur de tendance
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ fontSize: 16, color: '#4CAF50' }} />;
      case 'down':
        return <TrendingDownIcon sx={{ fontSize: 16, color: '#F44336' }} />;
      default:
        return <RemoveIcon sx={{ fontSize: 16, color: '#9E9E9E' }} />;
    }
  };

  // Formater une valeur avec son évolution
  const formatValueWithEvolution = (value, evolution, unit) => {
    if (!value) return <Typography variant="body2" sx={{ textAlign: 'center' }}>---</Typography>;

    // Déterminer la couleur de fond selon l'évolution
    let bgColor = 'transparent';
    if (evolution && evolution.trend === 'up') {
      bgColor = 'rgba(76, 175, 80, 0.15)'; // Vert clair pour amélioration
    } else if (evolution && evolution.trend === 'down') {
      bgColor = 'rgba(244, 67, 54, 0.15)'; // Rouge clair pour diminution
    }

    return (
      <Tooltip
        title={evolution ? `${evolution.diff > 0 ? '+' : ''}${evolution.diff} (${evolution.percentChange}%)` : ''}
        disableHoverListener={!evolution}
      >
        <Box sx={{
          backgroundColor: bgColor,
          borderRadius: 1,
          px: 1,
          py: 0.5,
          display: 'inline-block'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
            {value}{unit || ''}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  // Édition
  const handleEditClick = (medida) => {
    setCurrentMedida(medida);
    const edited = {
      fecha: medida.fecha ? new Date(medida.fecha).toISOString().split('T')[0] : '',
      fuente: medida.fuente || '',
      altura: medida.altura || '',
      envergadura: medida.envergadura || '',
      alcance: medida.alcance || '',
      salto_vertical_maximo: medida.salto_vertical_maximo || '',
      salto_vertical_de_pie: medida.salto_vertical_de_pie || '',
      peso: medida.peso || '',
      grasa_corporal: medida.grasa_corporal || '',
      longitud_mano: medida.longitud_mano || '',
      ancho_mano: medida.ancho_mano || '',
      agilidad: medida.agilidad || '',
      sprint_cuarto: medida.sprint_cuarto || ''
    };
    setEditedMedida(edited);

    // Initialiser les valeurs US
    const us = {};
    if (edited.altura) {
      const { feet, inches } = cmToFeetInches(edited.altura);
      us.alturaFeet = feet.toString();
      us.alturaInches = inches;
    }
    if (edited.envergadura) {
      const { feet, inches } = cmToFeetInches(edited.envergadura);
      us.envergaduraFeet = feet.toString();
      us.envergaduraInches = inches;
    }
    if (edited.alcance) {
      const { feet, inches } = cmToFeetInches(edited.alcance);
      us.alcanceFeet = feet.toString();
      us.alcanceInches = inches;
    }
    if (edited.salto_vertical_maximo) {
      us.saltoVerticalMaximoInches = cmToInches(edited.salto_vertical_maximo);
    }
    if (edited.salto_vertical_de_pie) {
      us.saltoVerticalDePieInches = cmToInches(edited.salto_vertical_de_pie);
    }
    if (edited.peso) {
      us.pesoLbs = kgToLbs(edited.peso);
    }
    if (edited.longitud_mano) {
      us.longitudManoInches = cmToInches(edited.longitud_mano);
    }
    if (edited.ancho_mano) {
      us.anchoManoInches = cmToInches(edited.ancho_mano);
    }
    setUsValues(us);
    setOpenEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!currentMedida || !editedMedida.fecha || !editedMedida.fuente) {
      alert(t('measurements.editDialog.requiredFields'));
      return;
    }
    
    const payload = Object.fromEntries(
      Object.entries(editedMedida).map(([key, value]) => [key, value || null])
    );
    
    try {
      await api.put(`/api/medidas/${currentMedida.id}`, payload);
      
      const updatedData = medidasData.map(item =>
        item.id === currentMedida.id ? { ...item, ...payload } : item
      ).sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
      
      setMedidasData(updatedData);
      setOpenEditDialog(false);
    } catch (error) {
      console.error(t('measurements.errors.updateError'), error);
      alert(t('measurements.errors.updateError'));
    }
  };

  // Suppression
  const handleDeleteClick = (medida) => {
    setMedidaToDelete(medida);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!medidaToDelete) return;
    try {
      await api.delete(`/api/medidas/${medidaToDelete.id}`);
      const filteredData = medidasData.filter(item => item.id !== medidaToDelete.id);
      setMedidasData(filteredData);
      if (onDataLoaded) {
        onDataLoaded(filteredData.length > 0);
      }
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error(t('measurements.errors.deleteError'), error);
      alert(t('measurements.errors.deleteError'));
    }
  };

  // Render mobile view
  const renderMobileView = () => (
    <Box sx={{ p: 1 }}>
      {medidasData.map((medida, index) => (
        <Card key={medida.id} sx={{ mb: 2, overflow: 'visible' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {medida.fuente}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(medida.fecha).toLocaleDateString()}
                  {dateNaissance && ` (${calculateDecimalAge(dateNaissance, medida.fecha)} ${t('measurements.years')})`}
                </Typography>
              </Box>
              <Box>
                <IconButton size="small" onClick={() => handleEditClick(medida)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteClick(medida)} sx={{ color: 'error.main' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              {metrics.map(metric => {
                const value = medida[metric.key];
                const previousValue = medidasData[index + 1]?.[metric.key];
                const evolution = calculateEvolution(value, previousValue, metric.inverse);

                // Déterminer la couleur de fond pour mobile
                let mobileBgColor = 'grey.50';
                if (evolution && evolution.trend === 'up') {
                  mobileBgColor = 'rgba(76, 175, 80, 0.1)';
                } else if (evolution && evolution.trend === 'down') {
                  mobileBgColor = 'rgba(244, 67, 54, 0.1)';
                }

                return value ? (
                  <Box key={metric.key} sx={{ p: 1, bgcolor: mobileBgColor, borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {metric.label}
                    </Typography>
                    <Tooltip
                      title={evolution ? `${evolution.diff > 0 ? '+' : ''}${evolution.diff} (${evolution.percentChange}%)` : ''}
                      disableHoverListener={!evolution}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {value}{metric.unit || ''}
                      </Typography>
                    </Tooltip>
                  </Box>
                ) : null;
              })}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  // Render desktop view
  const renderDesktopView = () => (
    <TableContainer component={Paper} sx={{ maxHeight: 600, boxShadow: 3 }}>
      <Table stickyHeader size="small">
        <TableHead>
          {/* Ligne des catégories */}
          <TableRow>
            <TableCell rowSpan={2} sx={{ fontWeight: 600, bgcolor: 'grey.100', textAlign: 'center', borderRight: '1px solid #e0e0e0' }}>{t('measurements.date')}</TableCell>
            <TableCell rowSpan={2} sx={{ fontWeight: 600, bgcolor: 'grey.100', textAlign: 'center', borderRight: '1px solid #e0e0e0' }}>{t('measurements.event')}</TableCell>
            <TableCell rowSpan={2} sx={{ fontWeight: 600, bgcolor: 'grey.100', textAlign: 'center', borderRight: '1px solid #e0e0e0' }}>{t('measurements.age')}</TableCell>
            {!isSmallScreen && (
              <>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'grey.100',
                    borderBottom: '2px solid #424242',
                    borderRight: '1px solid #e0e0e0',
                    fontSize: '0.85rem',
                    letterSpacing: '1px'
                  }}
                >
                  {t('measurements.categories.anthropometry')}
                </TableCell>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'grey.100',
                    borderBottom: '2px solid #424242',
                    borderRight: '1px solid #e0e0e0',
                    fontSize: '0.85rem',
                    letterSpacing: '1px'
                  }}
                >
                  {t('measurements.categories.performance')}
                </TableCell>
              </>
            )}
            <TableCell rowSpan={2} align="center" sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>{t('measurements.actions')}</TableCell>
          </TableRow>
          {/* Ligne des métriques individuelles */}
          <TableRow>
            {isSmallScreen ? null : metrics.map(metric => (
              <TableCell
                key={metric.key}
                align="center"
                sx={{
                  fontWeight: 600,
                  bgcolor: 'grey.100',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'grey.200' },
                  textAlign: 'center',
                  position: 'relative',
                  borderRight: metric.key === 'grasa_corporal' ? '1px solid #e0e0e0' : 'none'
                }}
                onMouseEnter={() => setHoveredMetric(metric.key)}
                onMouseLeave={() => setHoveredMetric(null)}
              >
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {metric.category}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        {metric.label} {metric.unit ? `(${metric.unit})` : ''}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', mb: 0.5 }}>
                        {t('measurements.examplePrefix')} {metric.example}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        ➤ {metric.description}
                      </Typography>
                    </Box>
                  }
                  placement="bottom"
                  arrow
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center' }}>
                      {metric.label}
                    </Typography>
                    {hoveredMetric === metric.key && (
                      <Zoom in>
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: metric.color }} />
                      </Zoom>
                    )}
                  </Box>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {medidasData.map((row, index) => (
            <TableRow 
              key={row.id}
              sx={{ 
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'background-color 0.2s'
              }}
            >
              <TableCell sx={{ textAlign: 'center' }}>
                <Typography variant="body2">
                  {new Date(row.fecha).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell sx={{ textAlign: 'center' }}>
                <Chip
                  label={row.fuente}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              </TableCell>
              <TableCell sx={{ textAlign: 'center' }}>
                <Typography variant="body2">
                  {calculateDecimalAge(dateNaissance, row.fecha)}
                </Typography>
              </TableCell>
              {isSmallScreen ? null : metrics.map(metric => {
                const value = row[metric.key];
                const previousValue = medidasData[index + 1]?.[metric.key];
                const evolution = calculateEvolution(value, previousValue, metric.inverse);

                return (
                  <TableCell
                    key={metric.key}
                    align="center"
                    sx={{
                      textAlign: 'center',
                      borderRight: metric.key === 'grasa_corporal' ? '1px solid #e0e0e0' : 'none'
                    }}
                  >
                    <Fade in timeout={500}>
                      <Box>
                        {formatValueWithEvolution(value, evolution, metric.unit)}
                      </Box>
                    </Fade>
                  </TableCell>
                );
              })}
              <TableCell align="center">
                <Tooltip title={t('measurements.edit')}>
                  <IconButton size="small" onClick={() => handleEditClick(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('measurements.delete')}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(row)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: { xs: 0, md: 2 } }}>
      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<ShowChartIcon />}
          iconPosition="start"
          label={t('measurements.tabs.evolution')}
        />
        <Tab
          icon={<PeopleIcon />}
          iconPosition="start"
          label={t('measurements.similar.tabTitle')}
          disabled={!medidasData.length}
        />
      </Tabs>

      {/* Tab 0: Evolution (existing content) */}
      {activeTab === 0 && (
        <>
          {!medidasData.length ? (
            <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Typography variant="h6" color="text.secondary">
                {t('measurements.noDataAvailable')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('measurements.measurementsWillAppear')}
              </Typography>
            </Card>
          ) : (
            <>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('measurements.evolutionTitle')}
                </Typography>
            <Chip
              label={`${medidasData.length} ${medidasData.length !== 1 ? t('measurements.recordsPlural') : t('measurements.records')}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          
          {isMobile ? renderMobileView() : renderDesktopView()}
          
          {/* Légende */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: 'rgba(76, 175, 80, 0.15)', borderRadius: 0.5 }} />
              <Typography variant="caption">{t('measurements.improvement')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: 'rgba(244, 67, 54, 0.15)', borderRadius: 0.5 }} />
              <Typography variant="caption">{t('measurements.decrease')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: 'transparent', border: '1px solid #9E9E9E', borderRadius: 0.5 }} />
              <Typography variant="caption">{t('measurements.noChange')}</Typography>
            </Box>
          </Box>

          {/* Graphique Radar Comparatif - Toujours visible */}
          {medidasData.length > 0 && (
            <Card sx={{ mt: 3, p: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('measurements.comparative.title')}
                </Typography>

                {loadingComparative ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : comparativeData ? (
                  <>
                    <Tabs
                      value={comparativeTypeFilter || ''}
                      onChange={(e, newVal) => {
                        setComparativeTypeFilter(newVal);
                        fetchComparativeData(newVal);
                      }}
                      sx={{ mb: 2, minHeight: 36 }}
                    >
                      <Tab label={t('measurements.comparative.all')} value="" sx={{ minHeight: 36, py: 0 }} />
                      <Tab label="Pro" value="pro" sx={{ minHeight: 36, py: 0 }} />
                      <Tab label="Cantera" value="cantera" sx={{ minHeight: 36, py: 0 }} />
                    </Tabs>

                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={`${t('measurements.comparative.position')}: ${comparativeData.playerInfo.position}`}
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={`${t('measurements.comparative.age')}: ${comparativeData.playerInfo.age} ${t('measurements.comparative.years')}`}
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={`${t('measurements.comparative.comparedWith')}: ${comparativeData.playerInfo.comparisonGroup}`}
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={`${comparativeData.playerInfo.totalPlayers} ${t('measurements.comparative.players')}`}
                        color="primary"
                      />
                    </Box>

                    {comparativeData.playerInfo.totalPlayers === 0 ? (
                  <Alert severity="info">
                    {t('measurements.comparative.noData')}
                  </Alert>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={400}>
                      <RadarChart data={comparativeData.radarData}>
                        <PolarGrid stroke="#e0e0e0" />
                        <PolarAngleAxis
                          dataKey="metric"
                          tick={{ fontSize: 12 }}
                        />
                        <PolarRadiusAxis
                          domain={[0, 100]}
                          tickCount={5}
                          tick={{ fontSize: 10 }}
                        />
                        <Radar
                          name={t('measurements.comparative.player')}
                          dataKey="player"
                          stroke={theme.palette.primary.main}
                          fill={theme.palette.primary.main}
                          fillOpacity={0.3}
                          strokeWidth={2}
                        />
                        <Radar
                          name={t('measurements.comparative.average')}
                          dataKey="average"
                          stroke={theme.palette.secondary.main}
                          fill={theme.palette.secondary.main}
                          fillOpacity={0.2}
                          strokeWidth={1.5}
                          strokeDasharray="5 5"
                        />
                        <Legend />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Box sx={{
                                  bgcolor: 'background.paper',
                                  p: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1
                                }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {data.metric}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    {t('measurements.comparative.player')}: {data.value || 'N/A'} ({data.player}° {t('measurements.comparative.percentile')})
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    {t('measurements.comparative.average')}: {data.avgValue?.toFixed(1) || 'N/A'}
                                  </Typography>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                      </RadarChart>
                    </ResponsiveContainer>

                    {/* Tabla de percentiles */}
                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                      {t('measurements.comparative.percentiles')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 1 }}>
                      {comparativeData.radarData.filter((item) => item.value != null).map((item) => {
                        const percentile = item.player;
                        let color = 'text.secondary';
                        if (percentile >= 75) color = 'success.main';
                        else if (percentile >= 50) color = 'info.main';
                        else if (percentile >= 25) color = 'warning.main';
                        else color = 'error.main';

                        return (
                          <Box
                            key={item.metric}
                            sx={{
                              p: 1,
                              bgcolor: 'grey.50',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            <Typography variant="caption">
                              {item.metric}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {item.rank != null && item.total != null && (
                                <Chip
                                  label={t('measurements.comparative.rankLabel', { rank: item.rank, total: item.total })}
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setRankingTypeFilter(comparativeTypeFilter || '');
                                    fetchRankingDetail(item.metricKey, item.metric, comparativeTypeFilter || '');
                                  }}
                                  sx={{
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    fontSize: '0.7rem'
                                  }}
                                />
                              )}
                              <Tooltip
                                title={t('measurements.comparative.percentileTooltip', { percentile })}
                                arrow
                                placement="top"
                              >
                                <Chip
                                  label={`${percentile}°`}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    color: color,
                                    bgcolor: 'background.paper'
                                  }}
                                />
                              </Tooltip>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                      </>
                    )}
                  </>
                ) : (
                  <Alert severity="info">
                    {t('measurements.comparative.loading')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}
            </>
          )}
        </>
      )}

      {/* Tab 1: Similar Players */}
      {activeTab === 1 && (
        <SimilarPlayersTab playerId={playerId} />
      )}

      {/* Dialog d'édition avec conversions US/EU */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: '92vh',
            maxHeight: '92vh'
          }
        }}
      >
        <DialogTitle sx={{ py: 1 }}>{t('measurements.editDialog.title')}</DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          {currentMedida && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <TextField
                  label={t('measurements.editDialog.dateLabel')}
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editedMedida.fecha}
                  onChange={e => setEditedMedida(prev => ({ ...prev, fecha: e.target.value }))}
                  size="small"
                />
                <TextField
                  label={t('measurements.editDialog.eventLabel')}
                  fullWidth
                  value={editedMedida.fuente}
                  onChange={e => setEditedMedida(prev => ({ ...prev, fuente: e.target.value }))}
                  placeholder={t('measurements.editDialog.eventPlaceholder')}
                  size="small"
                />
              </Box>

              {/* Grille de toutes les mesures sans titres de sections */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                {/* Altura sin zapatos */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('measurements.editDialog.heightLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="6"
                      value={usValues.alturaFeet || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setUsValues(prev => ({ ...prev, alturaFeet: value }));
                        const cm = feetInchesToCm(value, usValues.alturaInches);
                        setEditedMedida(prev => ({ ...prev, altura: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">ft</Typography>
                    <TextField
                      size="small"
                      placeholder="2.5"
                      value={usValues.alturaInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, alturaInches: value }));
                        const cm = feetInchesToCm(usValues.alturaFeet, value);
                        setEditedMedida(prev => ({ ...prev, altura: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.altura}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, altura: value }));
                        if (value) {
                          const { feet, inches } = cmToFeetInches(value);
                          setUsValues(prev => ({ ...prev, alturaFeet: feet.toString(), alturaInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Envergadura */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('measurements.editDialog.wingspanLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="7"
                      value={usValues.envergaduraFeet || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setUsValues(prev => ({ ...prev, envergaduraFeet: value }));
                        const cm = feetInchesToCm(value, usValues.envergaduraInches);
                        setEditedMedida(prev => ({ ...prev, envergadura: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">ft</Typography>
                    <TextField
                      size="small"
                      placeholder="2"
                      value={usValues.envergaduraInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, envergaduraInches: value }));
                        const cm = feetInchesToCm(usValues.envergaduraFeet, value);
                        setEditedMedida(prev => ({ ...prev, envergadura: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.envergadura}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, envergadura: value }));
                        if (value) {
                          const { feet, inches } = cmToFeetInches(value);
                          setUsValues(prev => ({ ...prev, envergaduraFeet: feet.toString(), envergaduraInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Alcance */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('measurements.editDialog.reachLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="8"
                      value={usValues.alcanceFeet || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setUsValues(prev => ({ ...prev, alcanceFeet: value }));
                        const cm = feetInchesToCm(value, usValues.alcanceInches);
                        setEditedMedida(prev => ({ ...prev, alcance: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">ft</Typography>
                    <TextField
                      size="small"
                      placeholder="10"
                      value={usValues.alcanceInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, alcanceInches: value }));
                        const cm = feetInchesToCm(usValues.alcanceFeet, value);
                        setEditedMedida(prev => ({ ...prev, alcance: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.alcance}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, alcance: value }));
                        if (value) {
                          const { feet, inches } = cmToFeetInches(value);
                          setUsValues(prev => ({ ...prev, alcanceFeet: feet.toString(), alcanceInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Peso */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('measurements.editDialog.weightLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="220"
                      value={usValues.pesoLbs || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, pesoLbs: value }));
                        const kg = lbsToKg(value);
                        setEditedMedida(prev => ({ ...prev, peso: kg.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">lbs</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.peso}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, peso: value }));
                        if (value) {
                          const lbs = kgToLbs(value);
                          setUsValues(prev => ({ ...prev, pesoLbs: lbs }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">kg</Typography>
                  </Box>
                </Box>

                {/* Longitud Mano */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('measurements.editDialog.handLengthLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="9"
                      value={usValues.longitudManoInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, longitudManoInches: value }));
                        const cm = inchesToCm(value);
                        setEditedMedida(prev => ({ ...prev, longitud_mano: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.longitud_mano}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, longitud_mano: value }));
                        if (value) {
                          const inches = cmToInches(value);
                          setUsValues(prev => ({ ...prev, longitudManoInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Ancho Mano */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('measurements.editDialog.handWidthLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="8"
                      value={usValues.anchoManoInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, anchoManoInches: value }));
                        const cm = inchesToCm(value);
                        setEditedMedida(prev => ({ ...prev, ancho_mano: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.ancho_mano}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, ancho_mano: value }));
                        if (value) {
                          const inches = cmToInches(value);
                          setUsValues(prev => ({ ...prev, anchoManoInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Grasa Corporal */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('measurements.editDialog.bodyFatLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="6.5"
                      value={editedMedida.grasa_corporal}
                      onChange={e => setEditedMedida(prev => ({ ...prev, grasa_corporal: e.target.value }))}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">%</Typography>
                  </Box>
                </Box>

                {/* Salto Vertical de Pie */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('measurements.editDialog.standingVerticalLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="32"
                      value={usValues.saltoVerticalDePieInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, saltoVerticalDePieInches: value }));
                        const cm = inchesToCm(value);
                        setEditedMedida(prev => ({ ...prev, salto_vertical_de_pie: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.salto_vertical_de_pie}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, salto_vertical_de_pie: value }));
                        if (value) {
                          const inches = cmToInches(value);
                          setUsValues(prev => ({ ...prev, saltoVerticalDePieInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Salto Vertical Máximo */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('measurements.editDialog.maxVerticalLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="38"
                      value={usValues.saltoVerticalMaximoInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, saltoVerticalMaximoInches: value }));
                        const cm = inchesToCm(value);
                        setEditedMedida(prev => ({ ...prev, salto_vertical_maximo: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.salto_vertical_maximo}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, salto_vertical_maximo: value }));
                        if (value) {
                          const inches = cmToInches(value);
                          setUsValues(prev => ({ ...prev, saltoVerticalMaximoInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Agilidad */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('measurements.editDialog.agilityLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="11.20"
                      value={editedMedida.agilidad}
                      onChange={e => setEditedMedida(prev => ({ ...prev, agilidad: e.target.value }))}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">s</Typography>
                  </Box>
                </Box>

                {/* Sprint 3/4 */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('measurements.editDialog.sprintLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="3.10"
                      value={editedMedida.sprint_cuarto}
                      onChange={e => setEditedMedida(prev => ({ ...prev, sprint_cuarto: e.target.value }))}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">s</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>{t('measurements.editDialog.cancel')}</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">
            {t('measurements.editDialog.save')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog de suppression  */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('measurements.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('measurements.deleteDialog.message')} {medidaToDelete?.fecha ? new Date(medidaToDelete.fecha).toLocaleDateString() : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('measurements.deleteDialog.cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('measurements.deleteDialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog de classement détaillé */}
      <Dialog
        open={rankingDialogOpen}
        onClose={() => {
          setRankingDialogOpen(false);
          setRankingDialogData(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t('measurements.comparative.rankingTitle', { metric: rankingMetricLabel })}
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={rankingTypeFilter}
            onChange={(e, newVal) => {
              setRankingTypeFilter(newVal);
              fetchRankingDetail(rankingMetricKey, rankingMetricLabel, newVal);
            }}
            sx={{ mb: 1, minHeight: 36 }}
          >
            <Tab label={t('measurements.comparative.all')} value="" sx={{ minHeight: 36, py: 0 }} />
            <Tab label="Pro" value="pro" sx={{ minHeight: 36, py: 0 }} />
            <Tab label="Cantera" value="cantera" sx={{ minHeight: 36, py: 0 }} />
          </Tabs>
          {loadingRanking ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : rankingDialogData && rankingDialogData.rankedList?.length > 0 ? (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('measurements.comparative.player')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{rankingMetricLabel}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rankingDialogData.rankedList.map((row, index) => (
                    <TableRow
                      key={`${row.playerId}-${index}`}
                      sx={{
                        bgcolor: row.isCurrentPlayer ? 'action.selected' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: row.isCurrentPlayer ? 700 : 400 }}>
                          {row.rank}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            src={`${getMediaBaseUrl()}/photos/${row.playerId}.png?${Date.now()}`}
                            alt={row.playerName}
                            sx={{ width: 28, height: 28 }}
                          />
                          <Link
                            to={`/players/${row.playerId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: row.isCurrentPlayer ? 700 : 400,
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {row.playerName}
                            </Typography>
                          </Link>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: row.isCurrentPlayer ? 700 : 400 }}>
                          {row.value}{rankingMetricUnit}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              {t('measurements.comparative.noRankingData')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRankingDialogOpen(false);
            setRankingDialogData(null);
          }}>
            {t('measurements.comparative.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedidasTab;